import React, { useState, useEffect, useRef } from 'react';
import { PieChart as PieChartIcon, Download, CheckCircle2, AlertTriangle, Activity } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../components/ui/utils';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import api from '../../../utils/api';
import { Asset, AssetStatus, AssetType } from '../../../types/asset';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { toast } from 'sonner';

const AssetStatusBreakdownReport: React.FC = () => {
    const [assets, setAssets] = useState<Asset[]>([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const reportRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchAssets = async () => {
            try {
                setLoading(true);
                const response = await api.get<Asset[]>(`/api/assets`);
                setAssets(response);
            } catch (error) {
                console.error('Error fetching assets for status report:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAssets();
    }, []);

    // Helper functions to categorize statuses
    const isOperational = (status: AssetStatus) => ['Assigned', 'Available'].includes(status);
    const isActive = (status: AssetStatus) => status === 'Assigned';
    const isAvailable = (status: AssetStatus) => status === 'Available';
    const hasIssues = (status: AssetStatus) => ['Needs Repair', 'Damaged', 'Lost'].includes(status);
    const inMaintenance = (status: AssetStatus) => ['In Maintenance', 'In Triage'].includes(status);
    const isFinalState = (status: AssetStatus) => ['Retired', 'Archived', 'Disposed'].includes(status);

    const getCategoryStatus = (status: AssetStatus) => {
        if (isOperational(status)) return 'Workflow (Operational)';
        if (inMaintenance(status)) return 'Maintenance';
        if (hasIssues(status)) return 'Issues';
        if (isFinalState(status)) return 'Final State';
        return 'Other';
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Assigned': return '#10b981'; // emerald-500
            case 'Available': return '#3b82f6'; // blue-500
            case 'In Maintenance':
            case 'In Triage': return '#f59e0b'; // amber-500
            case 'Needs Repair':
            case 'Damaged':
            case 'Lost': return '#ef4444'; // red-500
            default: return '#6b7280'; // gray-500
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'Workflow (Operational)': return '#10b981'; // emerald-500
            case 'Maintenance': return '#f59e0b'; // amber-500
            case 'Issues': return '#ef4444'; // red-500
            case 'Final State': return '#6b7280'; // gray-500
            default: return '#cbd5e1';
        }
    };

    // Calculate overall stats
    const totalAssets = assets.length;
    const operationalCount = assets.filter(a => isOperational(a.status)).length;
    const activeCount = assets.filter(a => isActive(a.status)).length;
    const availableCount = assets.filter(a => isAvailable(a.status)).length;
    const attentionCount = assets.filter(a => hasIssues(a.status) || inMaintenance(a.status)).length; // Combining issues and maintenance for the top box

    const operationalPercentage = totalAssets > 0 ? (operationalCount / totalAssets) * 100 : 0;

    // Data for Status Distribution Pie
    const statusCounts: Record<string, number> = {};
    assets.forEach(a => {
        statusCounts[a.status] = (statusCounts[a.status] || 0) + 1;
    });
    const statusPieData = Object.entries(statusCounts).map(([name, value]) => ({
        name,
        value,
        color: getStatusColor(name)
    })).filter(d => d.value > 0);

    // Data for Category Breakdown Pie
    const categoryCounts: Record<string, number> = {
        'Workflow (Operational)': 0,
        'Maintenance': 0,
        'Issues': 0,
        'Final State': 0
    };
    assets.forEach(a => {
        const cat = getCategoryStatus(a.status);
        if (categoryCounts[cat] !== undefined) categoryCounts[cat]++;
    });
    const categoryPieData = Object.entries(categoryCounts).map(([name, value]) => ({
        name,
        value,
        color: getCategoryColor(name)
    })).filter(d => d.value > 0); // Kept all categories to show matching labels in UI, but could filter

    // Data for Operational Status by Asset Type (Stacked Bar)
    const assetsByType: Record<string, { total: number, active: number, available: number, issues: number }> = {};
    assets.forEach(a => {
        if (!assetsByType[a.type]) {
            assetsByType[a.type] = { total: 0, active: 0, available: 0, issues: 0 };
        }
        assetsByType[a.type].total++;
        if (isActive(a.status)) assetsByType[a.type].active++;
        else if (isAvailable(a.status)) assetsByType[a.type].available++;
        else if (hasIssues(a.status) || inMaintenance(a.status)) assetsByType[a.type].issues++;
    });

    const typeBarData = Object.entries(assetsByType).map(([name, data]) => {
        // Normalize to 100% stack if needed, or use raw values depending on the UI shown
        // The screenshot shows Y-axis from 0 to 1 (which means 100% normalized)
        const total = data.total;
        return {
            name,
            Assigned: total > 0 ? data.active / total : 0,
            Available: total > 0 ? data.available / total : 0,
            Issues: total > 0 ? data.issues / total : 0,
            rawAssigned: data.active,
            rawAvailable: data.available,
            rawIssues: data.issues
        };
    });

    // Custom Tooltip for Stacked Bar
    const CustomBarTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 rounded-xl shadow-lg border border-gray-100 text-[11px] font-medium min-w-[150px]">
                    <p className="font-bold text-gray-900 mb-2 truncate">{label}</p>
                    {payload.map((entry: any, index: number) => {
                        let labelName = entry.dataKey === 'Issues' ? 'Issues/Maintenance' : entry.dataKey;
                        let rawValue = entry.payload[`raw${entry.dataKey}`];
                        return (
                            <div key={index} className="flex justify-between items-center gap-4 mt-1">
                                <span style={{ color: entry.color }}>{labelName} : </span>
                                <span className="text-gray-900 font-bold">{rawValue}</span>
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };


    const downloadPDF = async () => {
        const element = reportRef.current;
        if (!element) return;

        try {
            setExporting(true);
            toast.info('Generating PDF...');

            const originalId = element.id;
            const tempId = `pdf-target-${Date.now()}`;
            element.id = tempId;

            const canvas = await html2canvas(element, {
                scale: 3,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                windowWidth: element.scrollWidth,
                imageTimeout: 0,
                onclone: (clonedDoc) => {
                    const clonedElement = clonedDoc.getElementById(tempId);
                    if (clonedElement) {
                        const animateClasses = ['animate-in', 'fade-in', 'duration-500', 'animate-pulse'];
                        animateClasses.forEach(cls => clonedElement.classList.remove(cls));

                        const exportBtns = clonedElement.querySelectorAll('[data-export-btn]');
                        exportBtns.forEach(btn => {
                            (btn as HTMLElement).style.display = 'none';
                        });
                    }
                }
            });

            if (originalId) {
                element.id = originalId;
            } else {
                element.removeAttribute('id');
            }

            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            const pdf = new jsPDF('p', 'mm', 'a4');
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            pdf.save(`asset-status-breakdown-report-${new Date().toISOString().split('T')[0]}.pdf`);
            toast.success('PDF downloaded successfully');
        } catch (error) {
            console.error('Error generating PDF:', error);
            toast.error('Failed to generate PDF');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div ref={reportRef} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-50 rounded-lg">
                            <PieChartIcon size={20} className="text-gray-600" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 text-lg">Asset Status Breakdown Report</h3>
                            <p className="text-gray-500 text-sm font-medium">Operational readiness snapshot - How healthy is our asset base?</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2" data-export-btn>
                        <Button variant="outline" className="border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2" onClick={downloadPDF} disabled={exporting}>
                            <Download size={16} className={exporting ? 'animate-spin' : ''} />
                            <span>{exporting ? 'Exporting...' : 'PDF'}</span>
                        </Button>
                    </div>
                </div>

                {/* Top KPI Cards */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-green-50/50 border border-green-100 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Operational Assets</p>
                        <h3 className="text-3xl font-black text-green-600">{operationalCount}</h3>
                        <p className="text-[11px] font-bold text-green-600 mt-1">{operationalPercentage.toFixed(1)}% of fleet</p>
                    </div>
                    <div className="border border-gray-100 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Assigned</p>
                        <h3 className="text-3xl font-black text-emerald-500">{activeCount}</h3>
                        <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded mt-2 font-medium">In Use</span>
                    </div>
                    <div className="border border-gray-100 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Available</p>
                        <h3 className="text-3xl font-black text-blue-600">{availableCount}</h3>
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-2 font-medium">Ready</span>
                    </div>
                    <div className="bg-red-50/50 border border-red-100 rounded-2xl p-5 flex flex-col items-center justify-center text-center">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Needs Attention</p>
                        <h3 className="text-3xl font-black text-red-600">{attentionCount}</h3>
                        <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded mt-2 font-medium text-center">Action Required</span>
                    </div>
                </div>

                {/* Charts Area 1: Pies */}
                {!loading && (
                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Status Distribution */}
                        <div className="border border-gray-100 rounded-2xl p-6 shadow-sm flex flex-col">
                            <h4 className="text-sm font-bold text-gray-900 mb-6">Status Distribution</h4>
                            <div className="flex-1 flex items-center justify-center relative min-h-[250px]">
                                {statusPieData.length > 0 ? (
                                    <>
                                        <div className="h-full w-full absolute inset-0">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={statusPieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={0}
                                                        outerRadius={80}
                                                        stroke="#fff"
                                                        strokeWidth={2}
                                                        dataKey="value"
                                                    >
                                                        {statusPieData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip
                                                        formatter={(value: number, name: string) => {
                                                            const percent = totalAssets > 0 ? ((value / totalAssets) * 100).toFixed(1) : '0.0';
                                                            return [`${value} (${percent}%)`, name];
                                                        }}
                                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        {/* Floating Labels */}
                                        {statusPieData.map((entry, index) => {
                                            // Very basic calculation for outside label positioning to match screenshot vibe
                                            const RADIAN = Math.PI / 180;
                                            const startAngle = 90; // Starting from top? Default is 0. Recharts pie starts at 0, rotates CW. Let's just guess positions or use standard recharts labels.
                                            // Since static absolute positioning is hard without knowing angles, let's just lay them out randomly or use Recharts standard label if possible. For simplicity and following the screenshot, I'll let standard Recharts tooltip handle complex data, but add custom CSS labels.
                                            // Actually, the screenshot has specific labels around the pie. Recharts label={...} can do this.
                                            // I'll add an SVG text label to Pie.
                                            return null;
                                        })}
                                        {/* Since manual positioning is hard without angle data, we'll just put standard labels, or skip floating labels in code for now and rely on Tooltip/Legend. Let's add a legend instead as a fallback. */}
                                    </>
                                ) : (
                                    <p className="text-gray-400 text-sm">No data available for charts.</p>
                                )}
                            </div>

                            {/* Manual Legend as fallback if floating labels are hard */}
                            {statusPieData.length > 0 && (
                                <div className="flex flex-wrap justify-center gap-4 mt-6">
                                    {statusPieData.map((item, idx) => (
                                        <div key={idx} className="flex items-center gap-1.5">
                                            <span className="text-xs font-medium" style={{ color: item.color }}>{item.name}: {totalAssets > 0 ? ((item.value / totalAssets) * 100).toFixed(0) : 0}%</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Category Breakdown */}
                        <div className="border border-gray-100 rounded-2xl p-6 shadow-sm flex flex-col">
                            <h4 className="text-sm font-bold text-gray-900 mb-6">Category Breakdown</h4>
                            <div className="flex-1 flex flex-col items-center justify-center min-h-[250px]">
                                {categoryPieData.length > 0 ? (
                                    <>
                                        <div className="h-48 w-full">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={categoryPieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={0}
                                                        outerRadius={80}
                                                        stroke="#fff"
                                                        strokeWidth={2}
                                                        dataKey="value"
                                                    >
                                                        {categoryPieData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                                        ))}
                                                    </Pie>
                                                    <RechartsTooltip
                                                        formatter={(value: number) => [`${value} assets`]}
                                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                        {/* Bottom Legend */}
                                        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
                                            {Object.keys(categoryCounts).map((cat, idx) => {
                                                const val = categoryCounts[cat];
                                                const color = getCategoryColor(cat);
                                                return (
                                                    <div key={idx} className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                                                        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }}></div>
                                                        <span style={{ color: val > 0 ? color : 'inherit' }}>{cat} {val > 0 && `: ${val}`}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-gray-400 text-sm">No data available.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Charts Area 2: Bar */}
                {!loading && (
                    <div className="mt-6 border border-gray-100 rounded-2xl p-6 shadow-sm">
                        <h4 className="text-sm font-bold text-gray-900 mb-6">Operational Status by Asset Type</h4>
                        <div className="h-48 w-full mt-4">
                            {typeBarData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={typeBarData} layout="vertical" margin={{ top: 0, right: 20, left: 40, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                        <XAxis
                                            type="number"
                                            domain={[0, 1]}
                                            ticks={[0, 0.25, 0.5, 0.75, 1]}
                                            tick={{ fontSize: 10, fill: '#6B7280' }}
                                            axisLine={{ stroke: '#E5E7EB' }}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            dataKey="name"
                                            type="category"
                                            tick={{ fontSize: 11, fill: '#6B7280' }}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <RechartsTooltip content={<CustomBarTooltip />} cursor={{ fill: '#F3F4F6' }} />
                                        {/* Gray background for total bar width to represent 100% implicitly */}
                                        <Bar dataKey="Issues" stackId="a" fill="#ef4444" radius={[0, 0, 0, 0]} />
                                        <Bar dataKey="Available" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                                        <Bar dataKey="Assigned" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <p className="text-gray-400 text-sm">No asset type data available.</p>
                                </div>
                            )}
                        </div>
                        <div className="flex justify-center gap-6 mt-6">
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 bg-emerald-500 rounded-sm"></div>
                                <span className="text-xs font-medium text-emerald-600">Assigned</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
                                <span className="text-xs font-medium text-blue-600">Available</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
                                <span className="text-xs font-medium text-red-600">Issues/Maintenance</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Detailed Status Breakdown Lists */}
                {!loading && (
                    <>
                        <div className="mt-6 border border-gray-100 rounded-2xl p-6 shadow-sm">
                            <h4 className="text-sm font-bold text-gray-900 mb-6">Detailed Status Breakdown</h4>
                            <div className="space-y-4">
                                {statusPieData.sort((a, b) => b.value - a.value).map((status, index) => {
                                    const percent = totalAssets > 0 ? (status.value / totalAssets) * 100 : 0;
                                    return (
                                        <div key={index} className="flex items-center justify-between p-3 border border-gray-100 rounded-xl">
                                            <div className="flex items-center gap-3">
                                                <div className="w-3 h-3 rounded-md min-w-[12px]" style={{ backgroundColor: status.color }}></div>
                                                <span className="text-[13px] font-bold text-gray-900 min-w-[120px]">{status.name}</span>
                                            </div>
                                            <div className="flex items-center gap-8 justify-end w-full max-w-[400px]">
                                                <span className="text-[11px] font-medium text-gray-500">{status.value} assets</span>
                                                <div className="flex items-center gap-3 w-32">
                                                    <span className="text-[11px] font-medium text-gray-700 min-w-[36px] text-right">{percent.toFixed(1)}%</span>
                                                    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                                        <div className="h-full rounded-full" style={{ width: `${percent}%`, backgroundColor: status.color }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="mt-6 border border-gray-100 rounded-2xl p-6 shadow-sm">
                            <h4 className="text-sm font-bold text-gray-900 mb-6">Summary by Asset Type</h4>
                            <div className="space-y-4">
                                {Object.entries(assetsByType).map(([type, data], index) => {
                                    const opCount = data.active + data.available;
                                    const percent = data.total > 0 ? (opCount / data.total) * 100 : 0;
                                    return (
                                        <div key={index} className="p-4 border border-gray-100 rounded-xl flex items-center justify-between">
                                            <div>
                                                <p className="text-[13px] font-bold text-gray-900">{type}</p>
                                                <p className="text-[11px] text-gray-500 mt-1">{opCount}/{data.total} operational ({percent.toFixed(0)}%)</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="bg-emerald-50 border border-emerald-100 rounded px-2 py-1 text-[10px] font-medium text-emerald-700">
                                                    {data.active} Assigned
                                                </div>
                                                <div className="bg-blue-50 border border-blue-100 rounded px-2 py-1 text-[10px] font-medium text-blue-700">
                                                    {data.available} Available
                                                </div>
                                                {data.issues > 0 && (
                                                    <div className="bg-red-50 border border-red-100 rounded px-2 py-1 text-[10px] font-medium text-red-700">
                                                        {data.issues} Issues
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Fleet Health Score */}
                        <div className="mt-6 bg-gradient-to-br from-emerald-50/50 to-blue-50/50 border border-emerald-100/50 rounded-2xl p-8 flex flex-col items-center justify-center text-center shadow-sm">
                            <Activity className="text-blue-500 mb-4" size={32} />
                            <h4 className="text-sm font-bold text-gray-900 mb-2">Fleet Health Score</h4>
                            <div className="flex items-center justify-center gap-8 mt-2">
                                <div>
                                    <h2 className="text-4xl font-black text-emerald-600">{operationalPercentage.toFixed(0)}%</h2>
                                    <p className="text-xs font-medium text-gray-500 mt-1 uppercase tracking-wider">Operational Readiness</p>
                                </div>
                                <div className="text-left space-y-2 border-l border-gray-200/50 pl-8">
                                    <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                                        <CheckCircle2 size={14} className="text-emerald-500" />
                                        <span>{operationalCount} assets ready for deployment</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                                        <AlertTriangle size={14} className="text-amber-500" />
                                        <span>{assets.filter(a => inMaintenance(a.status)).length} in maintenance</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                                        <AlertTriangle size={14} className="text-red-500 fill-white" fill="currentColor" />
                                        <span>{attentionCount} require attention</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

            </div>
        </div>
    );
};

export default AssetStatusBreakdownReport;
